import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { ArrowRight, ArrowLeft, Check, Sparkles, ShieldCheck, Clock, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Valoracion } from '../components/Valoracion';
import api, { mensajeDeError, pesos } from '../lib/api';
import { toast } from 'sonner';

/**
 * Buscador guiado de profesional.
 *
 * Elegir psicólogo leyendo seis fichas es una mala primera experiencia justo
 * cuando la persona está pidiendo ayuda. Aquí responde cuatro preguntas y recibe
 * tres sugerencias, cada una con el motivo concreto por el que calza.
 *
 * Las respuestas no se guardan: viajan, ordenan la lista y se descartan.
 */
const EncuentraPage = () => {
  const [preguntas, setPreguntas] = useState(null);
  const [paso, setPaso] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const [respuestas, setRespuestas] = useState({
    motivo: '',
    franja: '',
    idioma: 'Español',
    presupuesto: 0,
  });

  useEffect(() => {
    api
      .get('/recomendaciones/preguntas')
      .then(({ data }) => setPreguntas(data))
      .catch((err) => toast.error(mensajeDeError(err, 'No pudimos cargar el buscador')));
  }, []);

  const responder = (campo, valor) => {
    setRespuestas((r) => ({ ...r, [campo]: valor }));
    setPaso((p) => p + 1);
  };

  const buscar = async (presupuesto) => {
    const finales = { ...respuestas, presupuesto };
    setRespuestas(finales);
    setBuscando(true);

    try {
      const { data } = await api.post('/recomendaciones', finales);
      setResultado(data);
      setPaso(4);
    } catch (err) {
      toast.error(mensajeDeError(err, 'No pudimos buscar profesionales'));
    } finally {
      setBuscando(false);
    }
  };

  const reiniciar = () => {
    setResultado(null);
    setPaso(0);
    setRespuestas({ motivo: '', franja: '', idioma: 'Español', presupuesto: 0 });
  };

  if (!preguntas) {
    return (
      <div className="py-24 text-center text-slate-500">Preparando el buscador…</div>
    );
  }

  const PASOS = 4;

  return (
    <div className="min-h-[80vh] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Encabezado */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-teal-700" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900">
            Encontremos a quien te acompañe
          </h1>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">
            Cuatro preguntas y te sugerimos tres profesionales. No guardamos tus respuestas.
          </p>
        </div>

        {/* Avance */}
        {paso < PASOS && (
          <div className="flex items-center gap-2 mb-8" role="progressbar" aria-valuenow={paso} aria-valuemin={0} aria-valuemax={PASOS}>
            {Array.from({ length: PASOS }, (_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i < paso ? 'bg-teal-600' : i === paso ? 'bg-teal-300' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        )}

        {/* Paso 1 · Motivo */}
        {paso === 0 && (
          <Pregunta titulo="¿Qué te trae por acá?" detalle="Elige lo que más se acerque. No hay respuesta correcta.">
            <div className="grid sm:grid-cols-2 gap-2">
              {preguntas.motivos.map((m) => (
                <Opcion
                  key={m.key}
                  titulo={m.label}
                  detalle={m.detail}
                  onClick={() => responder('motivo', m.key)}
                />
              ))}
            </div>
          </Pregunta>
        )}

        {/* Paso 2 · Horario */}
        {paso === 1 && (
          <Pregunta titulo="¿Cuándo te acomoda?" detalle="Buscamos entre quienes tengan horas en esa franja.">
            <div className="grid sm:grid-cols-2 gap-2">
              {preguntas.franjas.map((f) => (
                <Opcion key={f.key} titulo={f.label} onClick={() => responder('franja', f.key)} />
              ))}
            </div>
            <Atras onClick={() => setPaso(0)} />
          </Pregunta>
        )}

        {/* Paso 3 · Idioma */}
        {paso === 2 && (
          <Pregunta titulo="¿En qué idioma prefieres la sesión?">
            <div className="grid sm:grid-cols-2 gap-2">
              {preguntas.idiomas.map((i) => (
                <Opcion key={i} titulo={i} onClick={() => responder('idioma', i)} />
              ))}
            </div>
            <Atras onClick={() => setPaso(1)} />
          </Pregunta>
        )}

        {/* Paso 4 · Presupuesto */}
        {paso === 3 && (
          <Pregunta
            titulo="¿Cuánto puedes destinar por sesión?"
            detalle="Es solo para ordenar las sugerencias. Puedes saltarlo."
          >
            <div className="space-y-2">
              {[
                { v: 28000, t: 'Hasta $28.000' },
                { v: 33000, t: 'Hasta $33.000' },
                { v: 40000, t: 'Hasta $40.000' },
                { v: 0, t: 'No es un factor para mí' },
              ].map((o) => (
                <Opcion key={o.t} titulo={o.t} onClick={() => buscar(o.v)} deshabilitado={buscando} />
              ))}
            </div>
            <Atras onClick={() => setPaso(2)} />
          </Pregunta>
        )}

        {/* Resultados */}
        {paso === 4 && resultado && (
          <div>
            {resultado.recomendados.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="font-medium text-slate-900 mb-2">
                    No encontramos horas que calcen con lo que buscas
                  </p>
                  <p className="text-slate-600 mb-6">
                    Prueba con otra franja horaria, o mira el listado completo.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={reiniciar} variant="outline" className="rounded-full">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Volver a intentar
                    </Button>
                    <Link to="/profesionales">
                      <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full w-full">
                        Ver todos
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <h2 className="font-heading text-xl font-semibold text-slate-900">
                    {resultado.recomendados.length === 1
                      ? 'Encontramos a alguien para ti'
                      : `${resultado.recomendados.length} profesionales para ti`}
                  </h2>
                  <Button onClick={reiniciar} variant="ghost" size="sm" className="text-slate-600">
                    <RotateCcw className="w-4 h-4 mr-1.5" />
                    Cambiar respuestas
                  </Button>
                </div>

                <div className="space-y-4">
                  {resultado.recomendados.map((r, i) => (
                    <Sugerencia key={r.psychologist.id} datos={r} destacado={i === 0} />
                  ))}
                </div>

                {resultado.otros > 0 && (
                  <p className="text-center text-sm text-slate-500 mt-6">
                    Hay {resultado.otros} profesionales más disponibles.{' '}
                    <Link to="/profesionales" className="text-teal-700 underline">
                      Ver el listado completo
                    </Link>
                  </p>
                )}

                <p className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-8">
                  <ShieldCheck className="w-4 h-4 text-teal-600" />
                  {resultado.nota}
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Pregunta = ({ titulo, detalle, children }) => (
  <div>
    <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-1">{titulo}</h2>
    {detalle && <p className="text-slate-600 mb-5">{detalle}</p>}
    {!detalle && <div className="mb-5" />}
    {children}
  </div>
);

const Opcion = ({ titulo, detalle, onClick, deshabilitado }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={deshabilitado}
    className="w-full text-left p-4 rounded-xl border border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/40 transition-colors disabled:opacity-50"
  >
    <span className="block font-medium text-slate-900">{titulo}</span>
    {detalle && <span className="block text-sm text-slate-500 mt-0.5">{detalle}</span>}
  </button>
);

const Atras = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mt-5"
  >
    <ArrowLeft className="w-4 h-4" />
    Volver
  </button>
);

/** Un profesional sugerido, con las razones por las que calza. */
const Sugerencia = ({ datos, destacado }) => {
  const p = datos.psychologist;
  const primera = datos.first_slot ? parseISO(datos.first_slot.replace(' ', 'T')) : null;

  return (
    <Card className={destacado ? 'border-teal-300 shadow-sm' : 'border-slate-200'}>
      <CardContent className="p-5">
        {destacado && (
          <Badge className="bg-teal-600 text-white border-0 mb-3">La mejor coincidencia</Badge>
        )}

        <div className="flex gap-4">
          {p.profile_image && (
            <img
              src={p.profile_image}
              alt=""
              loading="lazy"
              className="w-16 h-16 rounded-xl object-cover flex-none bg-slate-100"
            />
          )}

          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-lg font-semibold text-slate-900">{p.name}</h3>
            <Valoracion rating={p.rating} cantidad={p.rating_count} className="mt-1" />

            <div className="flex flex-wrap gap-1.5 mt-3">
              {p.specialties.slice(0, 3).map((e) => (
                <Badge key={e} className="bg-slate-100 text-slate-700 border-0 text-xs">
                  {e}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Por qué lo sugerimos */}
        {datos.reasons.length > 0 && (
          <ul className="mt-4 space-y-1.5">
            {datos.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-teal-600 flex-none mt-0.5" />
                {r}
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-slate-100">
          <div>
            <p className="font-semibold text-slate-900">{pesos(p.price_from)}</p>
            {primera && (
              <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3" />
                Primera hora: {format(primera, "d 'de' MMM, HH:mm", { locale: es })}
              </p>
            )}
          </div>

          <Link to={`/agendar?profesional=${p.id}`}>
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              Agendar
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default EncuentraPage;
